import { RenderPass } from "@/core/pipeline/render-pass"
import { Texture } from "@/core/backends/webgl/texture"
import { Renderer } from "@/core/pipeline/renderer"
import { BrushMask } from "@/core/types"
import { computeBrushTextureSize } from "@/core/utils/brushing"
import { BrushMaskCacheEntry, MaskPainter } from "@/core/utils/mask-painter"

export class BrushMaskPass extends RenderPass {
	private cache: Record<string, BrushMaskCacheEntry> = {}

	private maskPainter: MaskPainter

	private lastPhotoId: string = ""

	private isAdditive: boolean = false

	constructor(renderer: Renderer, id: string) {
		super(renderer, id)

		this.maskPainter = new MaskPainter(renderer, false)
	}

	render(inputTexture: Texture) {
		const { renderer } = this
		const { photo } = renderer

		const groups = photo.adjustments.localAdjustments

		if (!groups) {
			return
		}

		if (this.lastPhotoId !== photo.id) {
			this.dispose()
			this.lastPhotoId = photo.id
		}

		for (const group of groups) {
			if (group.active === false) {
				continue
			}

			for (const mask of group.masks) {
				if (mask.active === false || mask.type !== "brush") {
					continue
				}

				this.renderMask(mask, inputTexture)
			}
		}
	}

	getMaskTexture(mask: BrushMask) {
		return this.cache[mask.id]?.texture
	}

	setAdditiveMode(isAdditive: boolean) {
		this.isAdditive = isAdditive
	}

	private createMaskCache(): BrushMaskCacheEntry {
		const renderer = this.renderer

		const [width, height] = computeBrushTextureSize(
			renderer.photo.imageState.originalImageSize[0],
			renderer.photo.imageState.originalImageSize[1],
		)

		const texture = renderer.backend.createTexture({
			label: "brushMask.maskTexture",
			format: "r8unorm",
			width,
			height,
		})

		return {
			texture,
			strokes: [],
		}
	}

	private renderMask(mask: BrushMask, inputTexture: Texture) {
		const cache = this.cache[mask.id] ?? this.createMaskCache()

		const hasNoChanges =
			mask.strokes.length === cache.strokes.length &&
			mask.strokes.every((stroke, i) => {
				return stroke.points.length === (cache.strokes[i]?.points.length ?? 0)
			})

		if (hasNoChanges === true) {
			return
		}

		this.renderer.performanceCounters.begin("renderBrushMaskAlpha")

		this.maskPainter.drawMask(mask.strokes, cache.texture, {
			isAdditive: this.isAdditive,
			inputTexture,
			cache,
		})

		this.renderer.performanceCounters.end("renderBrushMaskAlpha")

		cache.strokes = mask.strokes.map((stroke) => ({ ...stroke, points: [...stroke.points] }))
		this.cache[mask.id] = cache
	}

	dispose() {
		for (const id in this.cache) {
			this.cache[id]?.texture.delete()
		}

		this.cache = {}
	}
}
